import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import {
  Button, ListGroup, Spinner, Alert, Form,
} from 'react-bootstrap';
import EmailInboxService from '../Services/EmailInboxService';

const PAGE_SIZE = 10;

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

/**
 * Reusable email inbox browser panel.
 * Loads and displays recent emails from the SES inbound S3 bucket.
 * Auto-loads when mounted — parent controls visibility.
 * Supports search (client-side) and pagination (server cursor + client pages).
 *
 * Props:
 *   onSelect(html: string, email: object) — called when user clicks an email.
 *     `email` is the LIST ROW — `{ key, from, subject, date, snippet }` — and
 *     `key` is the S3 object key, which is the only durable name an email has
 *     here. A caller that wants to hand the email to a server (rather than
 *     paste its text into a box) needs that key, not the HTML: the body of a
 *     forwarded thread runs to megabytes, and work identified by a key can be
 *     repeated against the same input later while work identified by pasted
 *     HTML cannot. The argument was added for the AI Admin dry run; it is
 *     additive, so the three callers that take only `html` are unaffected.
 */
function EmailInboxPanel({ onSelect }) {
  const [allEmails, setAllEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [fetching, setFetching] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const hasFetched = useRef(false);

  const loadEmails = useCallback(async ({ nextCursor = null, append = false } = {}) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setAllEmails([]);
      setCursor(null);
      setPage(0);
    }
    setError('');
    try {
      const params = { limit: 25 };
      if (nextCursor) params.cursor = nextCursor;
      const { emails, cursor: nextPage } = await EmailInboxService.listEmails(params);
      setAllEmails((prev) => (append ? [...prev, ...emails] : emails));
      setCursor(nextPage);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load emails');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadEmails();
  }, [loadEmails]);

  // Reset to first page whenever search changes
  useEffect(() => {
    setPage(0);
  }, [search]);

  const filteredEmails = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allEmails;
    return allEmails.filter(
      (e) => e.from?.toLowerCase().includes(term)
        || e.subject?.toLowerCase().includes(term)
        || e.snippet?.toLowerCase().includes(term),
    );
  }, [allEmails, search]);

  const totalPages = Math.max(1, Math.ceil(filteredEmails.length / PAGE_SIZE));
  const pageEmails = filteredEmails.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSelect = async (email) => {
    setFetching(email.key);
    setError('');
    try {
      const content = await EmailInboxService.getEmail(email.key);
      if (onSelect) onSelect(content.html || content.text || '', email);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch email');
    } finally {
      setFetching(null);
    }
  };

  const handleRefresh = useCallback(() => {
    hasFetched.current = false;
    setSearch('');
    loadEmails();
  }, [loadEmails]);

  return (
    <div>
      {error && (
        <Alert variant="danger" className="py-1 px-2 small" dismissible onClose={() => setError('')}>
          {error}
          <Button variant="link" size="sm" className="p-0 ms-2" onClick={handleRefresh}>
            Retry
          </Button>
        </Alert>
      )}

      {loading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading emails...
        </div>
      )}

      {!loading && (
        <>
          {/* Search */}
          <Form.Control
            size="sm"
            type="text"
            placeholder="Search by sender, subject or snippet..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2"
          />

          {/* Header row */}
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted">
              {filteredEmails.length}
              {search ? ' matched' : ' loaded'}
            </small>
            <Button variant="link" size="sm" className="p-0 text-muted" onClick={handleRefresh}>
              <i className="fa fa-refresh me-1" />
              Refresh
            </Button>
          </div>

          {filteredEmails.length === 0 ? (
            <div className="text-center text-muted py-4">
              <i className="fa fa-inbox d-block mb-2" style={{ fontSize: '1.5em' }} />
              {search ? 'No emails match your search' : 'No recent emails in inbox'}
            </div>
          ) : (
            <ListGroup
              variant="flush"
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '6px',
              }}
            >
              {pageEmails.map((email) => (
                <ListGroup.Item
                  key={email.key}
                  action
                  onClick={() => handleSelect(email)}
                  disabled={!!fetching}
                  className="py-2 px-3"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <strong className="small text-truncate" style={{ maxWidth: '70%' }}>
                      {email.from}
                    </strong>
                    <small className="text-muted text-nowrap ms-2">
                      {fetching === email.key ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        timeAgo(email.date)
                      )}
                    </small>
                  </div>
                  <div className="small text-truncate">{email.subject}</div>
                  {email.snippet && (
                    <div className="small text-muted text-truncate">{email.snippet}</div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {/* Client-side page controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Prev
              </Button>
              <small className="text-muted">
                Page
                {' '}
                {page + 1}
                {' '}
                of
                {' '}
                {totalPages}
              </small>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </Button>
            </div>
          )}

          {/* Load more from server */}
          {cursor && (
            <div className="text-center mt-2">
              <Button
                variant="link"
                size="sm"
                disabled={loadingMore}
                onClick={() => loadEmails({ nextCursor: cursor, append: true })}
              >
                {loadingMore ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Loading...
                  </>
                ) : (
                  'Load more emails'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EmailInboxPanel;
